import _ from "lodash";
import { vocabularize } from "src/Common/vocab";
import { IoIosArrowRoundDown, IoIosArrowRoundUp } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { formatDate, getAvatarUrl, getImageUrl } from "src/Common/common";
import LoadMoreButton from "src/Components/Common/loadmorebutton";
import Loader from "src/Components/Common/Loader";
import { setPONumber } from "src/Redux/Slices/HeaderSlice";
import POStatusComponent from "./Status/postatuscomponent";
import { can, handleError } from "src/Common/function";
import POService from "src/Services/API/po.service";
import ConfirmDialog from "src/Common/confirmdialog";
import Highlight from "src/Components/Common/Highlight";

export const PO_SCROLL_STORAGE_KEY = "PO_SCROLL_RESTORE";

function PoTable({ data, keyword, branch, loadMore, loadMoreButtonShown, restoring, sort, deleteSuccess }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [poArray, setPOArray] = useState([]);
  //sorting
  const [sortByDate, setSortByDate] = useState(0);
  //deleting
  const [isDeleteConfirmDialogShown, setIsDeleteConfirmDialogShown] = useState(false);
  const [indexToDelete, setIndexToDelete] = useState(0);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    let poData = _.cloneDeep(data);
    for (let i = 0; i < poData.length; i++) {
      if (poData[i].wo?.assignedTechs) {
        let primaryTech = poData[i].wo.assignedTechs.find((obj) => obj.roleId === 0);
        if (primaryTech) {
          let currentIndex = poData[i].wo.assignedTechs.findIndex((obj) => obj.roleId === 0);
          poData[i].wo.assignedTechs.splice(currentIndex, 1);
          poData[i].wo.assignedTechs.splice(0, 0, primaryTech);
        }
        let techNames = poData[i].wo.assignedTechs.map((obj) => obj.user.name);
        poData[i].techNames = techNames.join(", ");
      }
    }
    setPOArray(poData);
  }, [data]);

  // Restore scroll position after coming back from the PO detail page.
  // PO.js fetches every previously loaded row in a single request; once the
  // saved PO renders, restore the exact scrollTop saved at click time.
  useEffect(() => {
    const saved = sessionStorage.getItem(PO_SCROLL_STORAGE_KEY);
    if (!saved) return;
    try {
      const { id, scrollTop } = JSON.parse(saved);
      if (poArray.some((order) => order.id === id)) {
        sessionStorage.removeItem(PO_SCROLL_STORAGE_KEY);
        setTimeout(() => {
          const container = scrollContainerRef.current;
          if (container && typeof scrollTop === "number") {
            container.scrollTop = scrollTop;
          } else {
            document.getElementById(`po-row-${id}`)?.scrollIntoView({ block: "center", inline: "nearest" });
          }
        }, 0);
      }
    } catch {
      sessionStorage.removeItem(PO_SCROLL_STORAGE_KEY);
    }
  }, [poArray]);

  const deletePurchaseOrder = () => {
    POService.deleteById(poArray[indexToDelete].id)
      .then((response) => {
        console.log("delete res: ", response);
        if (response.status == 200) {
          setIsDeleteConfirmDialogShown(false);
          deleteSuccess(indexToDelete);
          toast("Purchase order deleted successfully");
        }
      })
      .catch((error) => {
        setIsDeleteConfirmDialogShown(false);
        handleError(error);
      });
  };

  return (
    <>
      <ConfirmDialog
        title="DELETE PURCHASE ORDER"
        description="Are you sure you want to delete this?"
        shown={isDeleteConfirmDialogShown}
        onConfirmClicked={deletePurchaseOrder}
        onCancelClicked={() => setIsDeleteConfirmDialogShown(false)}
      />
      <div
        className="relative w-full flex-1 min-h-0 flex flex-col overflow-hidden"
        style={{
          borderRadius: "12px",
          border: "1px solid #E5E8F3",
          background: "#FFF",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        {restoring && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70">
            <Loader variant="block" />
          </div>
        )}
        <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
          <table className="w-full min-w-[1450px]">
            <thead className="sticky top-0 z-10">
              <tr
                style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}
                className="[&>th]:border-r [&>th]:border-borderColor [&>th:last-child]:border-r-0"
              >
                <th>
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center w-[80px] h-[44px] text-[#5F6477] text-center">
                    <span>PO#</span>
                  </div>
                </th>
                <th>
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[180px] h-[44px] text-[#5F6477] justify-center">
                    <span>Vendor</span>
                  </div>
                </th>
                <th className="w-full">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 h-[44px] text-[#5F6477] min-w-[200px]">
                    Description
                  </div>
                </th>
                <th className="w-full">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[150px] h-[44px] text-[#5F6477] justify-left">
                    Issued For
                  </div>
                </th>
                <th>
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-1 w-[80px] h-[44px] text-[#5F6477]">
                    {vocabularize("Parent WO#")}
                  </div>
                </th>
                <th>
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[200px] h-[44px] text-[#5F6477] justify-left">
                    Customer
                  </div>
                </th>
                <th>
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[160px] h-[44px] text-[#5F6477]">
                    Issued By
                  </div>
                </th>

                <th
                  onClick={() => {
                    if (sortByDate === 1) {
                      setSortByDate(0);
                      sort({ date: 0 });
                    } else if (sortByDate === 0) {
                      setSortByDate(1);
                      sort({ date: 1 });
                    }
                  }}
                  className="cursor-pointer"
                >
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center justify-center gap-1 w-[120px] h-[44px] text-[#5F6477]">
                    <span>Date Added</span>
                    {sortByDate === 0 ? <IoIosArrowRoundUp /> : <IoIosArrowRoundDown />}
                  </div>
                </th>

                {branch === 0 && (
                  <th>
                    <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[140px] h-[44px] text-[#5F6477]">
                      Branch
                    </div>
                  </th>
                )}

                <th>
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center pl-4 w-[120px] h-[44px] text-[#5F6477]">
                    Status
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {poArray.map((order, index) => (
                <tr
                  key={index}
                  id={`po-row-${order.id}`}
                  onClick={() => {
                    sessionStorage.setItem(
                      PO_SCROLL_STORAGE_KEY,
                      JSON.stringify({
                        id: order.id,
                        offset: index,
                        loadedCount: poArray.length,
                        scrollTop: scrollContainerRef.current?.scrollTop ?? 0,
                        search: window.location.search,
                      })
                    );
                    dispatch(setPONumber(String(order.number)));
                    navigate("/purchase/" + String(order.id), {
                      state: { order: order },
                    });
                  }}
                  className="border-b border-borderColor cursor-pointer hover:bg-[#FAFAFB] [&>td]:border-r [&>td]:border-borderColor [&>td:last-child]:border-r-0"
                >
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-center text-[13px] leading-[20px] font-[400] text-[#1A2332] col-span-1">
                      <Highlight text={order.number} keyword={keyword} />
                    </div>
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-center text-[13px] leading-[20px] font-[400] text-[#1A2332] col-span-1">
                      <Highlight text={order.vendor} keyword={keyword} />
                    </div>
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                      <Highlight text={order.description} keyword={keyword} />
                    </div>
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-left text-[13px] leading-[20px] font-[400] text-[#1A2332] col-span-1 word-break-break-word">
                      <Highlight text={order.issuedUser?.name} keyword={keyword} />
                    </div>
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-center text-[13px] leading-[20px] font-[400] text-[#1A2332]">
                      {order.wo?.id ? (
                        <span
                          onClick={(event) => {
                            event.stopPropagation();
                            window.open("/work/" + String(order.wo.id), "_blank");
                          }}
                          className="text-blueColor cursor-pointer hover:underline"
                        >
                          <Highlight text={order.wo?.number} keyword={keyword} />
                        </span>
                      ) : (
                        <Highlight text={order.wo?.number} keyword={keyword} />
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-left text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                      <Highlight text={order.wo?.customer?.companyName} keyword={keyword} />
                    </div>
                  </td>
                  <td>
                    {order.issuedBy && (
                      <div className="flex items-center gap-1 px-4 py-3">
                        <img
                          src={getAvatarUrl(order.issuedBy.avatar)}
                          className="w-[20px] rounded-[10px] cursor-pointer"
                        />
                        <span className="font-OpenSansRegular text-[13px] leading-[20px] text-[#1A2332]">
                          <Highlight text={order.issuedBy.name} keyword={keyword} />
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[13px] leading-[20px] font-[400] text-[#1A2332] text-center">
                      {formatDate(order.createdAt)}
                    </div>
                  </td>

                  {branch === 0 && (
                    <td>
                      <div className="font-OpenSansRegular px-4 py-3 text-left text-[13px] leading-[20px] font-[400] text-[#1A2332] word-break-break-word">
                        {order.wo?.branch?.name || order.wo?.branch || ""}
                      </div>
                    </td>
                  )}

                  <td>
                    <div className="px-4 py-3 flex items-center gap-3">
                      <POStatusComponent value={order.status} />
                      {can("po:delete") && (
                        <img
                          onClick={(event) => {
                            setIsDeleteConfirmDialogShown(!isDeleteConfirmDialogShown);
                            setIndexToDelete(index);
                            event.stopPropagation();
                          }}
                          src={getImageUrl("/images/common/trash.png")}
                          className="cursor-pointer"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loadMoreButtonShown && (
            <div className="w-full mt-[10px] mb-[10px] flex justify-center">
              <LoadMoreButton onClicked={loadMore} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PoTable;
